import { useFlags } from "launchdarkly-react-client-sdk";
import { Navigate, useLocation, useParams } from "src/router";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { PageSpinner } from "src/components/loading";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useUser } from "src/contexts/user-context";
import { isPermissionsTabAccessible } from "src/events/permissions-tab-access";
import { lazyNamed } from "src/utils/lazy-named";
import { useContractOutletContextIfAvailable } from "./outlet";

const eventContractsImport = () => import("./contracts");
const EventContracts = lazyNamed(eventContractsImport, "EventContracts");

export const preloads: RoutePreload[] = [
  ["/events/contracts", eventContractsImport],
];
const SchemaIdRedirect = lazyNamed(
  () => import("./contract/event-schema/schema-id-redirect"),
  "SchemaIdRedirect",
);
const ContractLoader = lazyNamed(
  () => import("./contract/contract"),
  "ContractLoader",
);
const ContractDetails = lazyNamed(
  () => import("./contract/contract"),
  "ContractDetails",
);
const CreateEventSchema = lazyNamed(
  () => import("./contract/event-schema/create-event-schema"),
  "CreateEventSchema",
);
const EventSchema = lazyNamed(
  () => import("./contract/event-schema/event-schema"),
  "EventSchema",
);
const EventContractPermissions = lazyNamed(
  () => import("./contract/grants"),
  "EventContractPermissions",
);

export const EventContractPermissionsRoute = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const { eventsRbacEnabled } = useFlags();
  const contractContext = useContractOutletContextIfAvailable();
  const contract = contractContext?.contract;
  const permissions = useResourcePermission(
    contract == null
      ? undefined
      : {
          v2: {
            resource: "event_plan",
            grant: "can_update",
            id: contract.id,
          },
        },
  );
  const showPermissions = isPermissionsTabAccessible({
    eventsRbacEnabled,
    resource: contract,
    user,
    permissions,
  });

  if (!eventsRbacEnabled) {
    return (
      <Navigate
        to={{ pathname: "..", search: location.search }}
        relative="path"
        replace
      />
    );
  }

  if (!contract) {
    return id ? <PageSpinner /> : <Navigate to="/events/contracts" replace />;
  }

  if (permissions.isLoading) {
    return <PageSpinner />;
  }

  if (!showPermissions) {
    return (
      <Navigate
        to={{ pathname: "..", search: location.search }}
        relative="path"
        replace
      />
    );
  }

  return <EventContractPermissions />;
};

export const routes: Array<RouteObject> = [
  {
    index: true,
    spacesAccess: "hidden-from-limited-view",
    element: <EventContracts />,
  },
  {
    path: "schemas/:event_schema_id",
    spacesAccess: "hidden-from-limited-view",
    element: <SchemaIdRedirect />,
  },
  {
    path: ":id",
    spacesAccess: "hidden-from-limited-view",
    element: <ContractLoader />,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <ContractDetails />,
      },
      {
        path: "new_event",
        spacesAccess: "hidden-from-limited-view",
        element: <CreateEventSchema />,
      },
      {
        path: ":event_type_and_name/:event_version",
        spacesAccess: "hidden-from-limited-view",
        element: <EventSchema />,
      },
      {
        path: "permissions",
        spacesAccess: "hidden-from-limited-view",
        element: <EventContractPermissionsRoute />,
      },
    ],
  },
];
